<script lang="ts">
    import FormErrors from '$components/Form/FormErrors.svelte';
    import HtmlForm from '$components/Form/HtmlForm.svelte';
    import {
        createForm,
        type FeedPostFormData,
    } from '$components/Feed/FeedPostFormData.js';
    import {
        LinkMetaDataFragmentDoc,
        MediaImageFragmentDoc,
        type NewsFeedPostFragment,
        type ParticipantInput,
    } from '$gql/graphql.js';
    import { getContainer } from '$components/Context/ContainerContext.js';
    import { createOnSubmit, updateOnSubmit } from './FeedPostFormAction';
    import Row from '$components/Form/Row.svelte';
    import SubmitButton from '$components/Form/SubmitButton.svelte';
    import { cx } from '$lib/utils/cx.js';
    import isEqual from 'lodash/isEqual';
    import filter from 'lodash/filter';
    import debounce from 'lodash/debounce';
    import { useFragment } from '$gql/fragment-masking.js';
    import { categoryItems } from './Utils.js';
    import ButtonEmojiPicker from '$components/Emoji/ButtonEmojiPicker.svelte';
    import type { Emoji } from '$components/Emoji/EmojiPicker.svelte';
    import LinkCollection from '$components/LinkMetaData/LinkCollection.svelte';
    import { extractLinks } from '$components/LinkMetaData/Utils.js';
    import MediaPreview from './MediaPreview.svelte';
    import MediaUploading from './MediaUploading.svelte';
    import MentionInput from './MentionInput.svelte';
    import { createEventDispatcher, tick } from 'svelte';
    import DatePickerInput, {
        zonedNullable,
    } from '$components/Date/DatePickerInput.svelte';
    import { keepIsFailure, keepIsOk } from '$lib/utils/filterNotNull.js';
    import { fromZonedDateTimeStringToLocalDateOrNull } from '$lib/utils/zonedDateTime.js';
    import { isAfter } from 'date-fns';
    import WithTooltip from '$components/Tooltip/WithTooltip.svelte';
    import { melt } from '@melt-ui/svelte';
    import * as t from '$trans';
    import Button, {
        defaultButtonClass,
        defaultIconClass,
    } from './FeedPostForm/Button.svelte';
    import Dropdown from './FeedPostForm/Dropdown.svelte';
    import SelectMyParticipantInput from '$components/Feed/SelectMyParticipant/SelectMyParticipantInput.svelte';
    import VideoModal, {
        createVideoModal,
    } from '$components/VideoModal/VideoModal.svelte';

    export let context: string = 'feed_media';
    export let formName = 'feed-post-form';
    export let initialAuthor: ParticipantInput;
    export let initialBody: string = '';
    export let sharedPostId: number | null = null;
    export let post: NewsFeedPostFragment | null = null;
    export let relatedCategoryId: number | null = null;
    export let autoFocus = false;
    export let mobile = false;
    export let country: string | null = null;

    const uploadApi = getContainer().uploadApi();

    const dispatch = createEventDispatcher<{ success: void }>();

    const container = getContainer();
    const { toaster } = container;
    const {
        helpers: { addToast },
    } = toaster();
    const { lang } = getContainer();

    let inputFileElement: HTMLInputElement;
    let bodyInputComponent: MentionInput<FeedPostFormData, string> | null = null;

    let links: string[] = [];

    let pendingPublicationDate = fromZonedDateTimeStringToLocalDateOrNull(
        post?.pendingPublicationDate ?? null,
    );
    let isStillPending =
        pendingPublicationDate == null
            ? false
            : isAfter(pendingPublicationDate.toDate(), new Date());

    let [form, fields] = createForm(
        formName,
        {
            author: initialAuthor,
            body: (post?.body || initialBody) ?? [],
            medias:
                post?.medias.map((m) => useFragment(MediaImageFragmentDoc, m).id) ?? [],
            linkMetaDatas:
                post?.linkMetaDatas.map(
                    (l) => useFragment(LinkMetaDataFragmentDoc, l).id,
                ) ?? [],
            tag: post?.tag ?? null,
            startDate: fromZonedDateTimeStringToLocalDateOrNull(
                post?.eventStartDate ?? null,
            ),
            endDate: fromZonedDateTimeStringToLocalDateOrNull(post?.eventEndDate ?? null),
            pendingPublicationDate: isStillPending ? pendingPublicationDate : null,
            withPendingPublicationDate: isStillPending,
            relatedCategory:
                post != null ? (post.relatedCategory?.id ?? null) : relatedCategoryId,
            country: post != null ? (post.country ?? null) : country,
        },
        sharedPostId,
    );

    $: formValues = form.stores().formValues();
    $: withPendingPublicationDate = form
        .stores()
        .fieldValue(fields.withPendingPublicationDate);

    $: onSubmit =
        post == null ? createOnSubmit(container) : updateOnSubmit(post.id, container);

    let uploadingCount = 0;

    async function onNewMedia(mediaList: (string | File)[]) {
        const promises = mediaList.map((media) => {
            if (typeof media === 'string') {
                return uploadApi.uploadVideoLink({
                    link: media,
                    context,
                });
            }
            return uploadApi.uploadMedia({ file: media, context });
        });

        uploadingCount += promises.length;
        const mediaResults = await Promise.all(promises);
        const mediaErrors = keepIsFailure(mediaResults);
        const mediaIds = keepIsOk(mediaResults);

        form.dispatch(
            form
                .actions()
                .changeValue([...$formValues.medias, ...mediaIds], fields.medias),
        );

        if (mediaErrors.length > 0) {
            addToast({
                data: {
                    type: 'error',
                    message: t.feed_post.toast_media_upload_error({}, $lang),
                },
            });
        }

        uploadingCount -= promises.length;
    }

    function onEmojiSelect(emoji: Emoji) {
        const native = emoji.native;
        if (native != null) {
            bodyInputComponent?.insertEmoji(native);
        }
    }

    const bodyDebouncer = debounce(function (value: string) {
        let list = extractLinks(value);
        if (
            !(
                list.length === 0 &&
                $formValues.linkMetaDatas.length > 0 &&
                links.length > 0
            )
        ) {
            links = list;
        }
    }, 500);
    $: bodyDebouncer($formValues.body);

    let dragMedia: number | null = null;
    let dragOverMedia: number | null = null;

    function dragStart(_e: DragEvent, position: number) {
        dragMedia = position;
    }

    function dragEnter(_e: DragEvent, position: number) {
        dragOverMedia = position;
    }

    function drop(_e: DragEvent) {
        const tempList = [...$formValues.medias];
        if (dragMedia != null && dragOverMedia != null) {
            const dragMediaContent = tempList[dragMedia];
            tempList.splice(dragMedia, 1);
            tempList.splice(dragOverMedia, 0, dragMediaContent!);
        }

        dragMedia = null;
        dragOverMedia = null;
        form.dispatch(form.actions().changeValue(tempList, fields.medias));
    }

    function toggleWithPendingPublicationDate() {
        form.dispatch((state) => {
            const bool = fields.withPendingPublicationDate.getValue(state.values);
            return form.actions().changeValue(!bool, fields.withPendingPublicationDate)(
                state,
            );
        });
    }

    const videoModal = createVideoModal({});
    const {
        elements: { trigger: videoModalTrigger },
    } = videoModal;

    let key = 1;
</script>

{#key key}
    <HtmlForm
        class="grow bg-white rounded-[16px] p-[16px] pb-[15px] shadow-leo"
        {form}
        {onSubmit}
        onSubmitSuccess={({ form }) => {
            form.dispatch((_type) => form.getInitialState());
            addToast({
                data: {
                    type: 'success',
                    message: t.feed_post.toast_post_saved_success({}, $lang),
                },
            });

            links = [];
            dispatch('success');
            tick().then(() => {
                key++;
            });
        }}
    >
        <div class={cx('flex mb-[12px] space-x-[12px] items-start')}>
            <SelectMyParticipantInput {form} field={fields.author} />

            <div class="grow flex flex-col gap-2">
                <Row {form} class="w-full" field={fields.body} label={null} let:field>
                    <MentionInput
                        bind:this={bodyInputComponent}
                        {form}
                        {field}
                        toText={(id) => id}
                        fromText={(id) => id}
                        class={cx(
                            'rounded-[4px] border-solid border border-gray-350 bg-gray-100 p-[8px] placeholder-gray-500 text-black text-14/[20px] font-normal not-italic',
                            mobile ? 'min-h-[248px]' : '',
                        )}
                        {autoFocus}
                        placeholder="Que souhaitez-vous partager aujourd’hui ?"
                    />
                </Row>
                {#if $withPendingPublicationDate}
                    <Row
                        {form}
                        field={fields.pendingPublicationDate}
                        label={'Date et heure de publication'}
                        let:field
                    >
                        <DatePickerInput
                            futureDateOnly
                            {field}
                            {form}
                            {...zonedNullable}
                        />
                    </Row>
                {/if}
                {#if $formValues.tag === 'event'}
                    <Row
                        class="grow mb-4"
                        {form}
                        field={fields.startDate}
                        label={'Date de début'}
                        required
                        let:field
                    >
                        <DatePickerInput {field} {form} {...zonedNullable} />
                    </Row>
                    <Row
                        class="grow mb-4"
                        {form}
                        field={fields.endDate}
                        label={'Date de fin'}
                        let:field
                    >
                        <DatePickerInput {field} {form} {...zonedNullable} />
                    </Row>
                {/if}
            </div>
        </div>

        <LinkCollection
            {links}
            {sharedPostId}
            on:ids={(event) => {
                let ids = event.detail;
                if (sharedPostId === null) {
                    form.dispatch(form.actions().changeValue(ids, fields.linkMetaDatas));
                } else if (!isEqual(ids, $formValues.linkMetaDatas)) {
                    form.dispatch(form.actions().changeValue(ids, fields.linkMetaDatas));
                }
            }}
        />

        {#if $formValues.medias.length > 0 || uploadingCount > 0}
            <div class={'flex items-center space-x-5 overflow-x-auto max-w-full py-4'}>
                {#each $formValues.medias as media, index (media)}
                    <!-- svelte-ignore a11y-no-static-element-interactions -->
                    <div
                        on:dragstart={(e) => dragStart(e, index)}
                        on:dragenter={(e) => dragEnter(e, index)}
                        on:dragend={drop}
                        draggable={true}
                    >
                        <MediaPreview
                            mediaId={media}
                            on:remove={() =>
                                form.dispatch(
                                    form.actions().changeValue(
                                        filter($formValues.medias, (m) => m !== media),
                                        fields.medias,
                                    ),
                                )}
                        />
                    </div>
                {/each}

                {#each Array(uploadingCount) as _value, index (index)}
                    <MediaUploading />
                {/each}
            </div>
        {/if}

        <div
            class={'flex flex-col ls:flex-row items-start ls:items-center gap-[16px] justify-between'}
        >
            <div
                class={'w-full ls:w-auto flex flex-wrap ls:flex-nowrap items-start grow gap-x-[8px] gap-y-[12px]'}
            >
                <input
                    type="file"
                    bind:this={inputFileElement}
                    multiple={true}
                    accept={'.jpeg, .png, .jpg, .webp'}
                    class={'hidden'}
                    on:change={(e) => onNewMedia([...(e.currentTarget.files ?? [])])}
                />
                <Button
                    onClick={() => inputFileElement.click()}
                    icon={'tnp tnp-gallery'}
                    text={t.feed_post.input_photo_label({}, $lang)}
                />
                <Button
                    meltElement={$videoModalTrigger}
                    icon={'tnp tnp-video'}
                    text={t.feed_post.input_video_label({}, $lang)}
                />
                <VideoModal modal={videoModal} onUrl={(url) => onNewMedia([url])} />
                <ButtonEmojiPicker
                    onSelect={onEmojiSelect}
                    autocloseAfterSelect={true}
                    class={defaultButtonClass}
                >
                    <i class={cx(defaultIconClass, 'tnp tnp-emoji')} aria-hidden={true} />
                </ButtonEmojiPicker>

                {#if post == null || post?.pendingPublicationDate != null}
                    <WithTooltip
                        tooltip="Programmer un post"
                        openDelay={0}
                        closeDelay={0}
                    >
                        <button
                            slot="default"
                            let:trigger
                            use:melt={trigger}
                            type="button"
                            on:click={() => {
                                toggleWithPendingPublicationDate();
                            }}
                            class={defaultButtonClass}
                        >
                            <i
                                class="{defaultIconClass} tnp"
                                class:tnp-cross-small={$withPendingPublicationDate}
                                class:tnp-calendar-clock={!$withPendingPublicationDate}
                            ></i>
                        </button>
                    </WithTooltip>
                {/if}

                <Dropdown
                    items={categoryItems($lang)}
                    placeholder="Catégorie"
                    {form}
                    field={fields.tag}
                />

                <div class={'ls:grow basis-full ls:basis-auto'}></div>
            </div>
            <SubmitButton
                {form}
                layout="stable"
                class="w-full ls:w-auto btn-leo btn-leo-primary btn-sm self-start text-14/[18px] border py-[8px] px-[24px]"
                text={t.ui.to_post({}, $lang)}
                submittingText={t.ui.is_posting({}, $lang)}
            />
        </div>

        <div class="flex flex-col gap-y-8">
            <FormErrors {form} />
        </div>
    </HtmlForm>
{/key}
